// Pure client-side sort + text-filter for the shared RegisterPage list (Session AD, Item 2). Every
// register (Customers, Sites, Lifts) gets column sorting and a text search from one place: a column
// opts in by providing a `sortValue` accessor; the search matches the text of every column that has
// one. JSX-free so the page and its tests share one source of truth (the assetFilters.ts pattern).

export type SortDir = "asc" | "desc";

// The minimal column shape this module needs (structurally a subset of RegisterPage's ColumnDef<T>,
// so a ColumnDef<T>[] is accepted directly). `sortValue` returns the comparable/searchable value for
// a row, or null/undefined when the column is not sortable/searchable.
export interface SortableColumn<T> {
  key: string;
  sortValue?: (row: T) => string | number | null | undefined;
}

export interface TableView {
  sortKey: string | null; // the active sort column key, or null for the default (load) order
  sortDir: SortDir;
  search: string;
}

// Compare two sort values for ascending order. Nulls/blanks always sort LAST regardless of
// direction (an empty cell is never "smallest"). Numbers compare numerically; strings compare with
// a locale-aware, numeric-aware comparison so "ELE-9" sorts before "ELE-10". Pure.
export function compareSortValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // a after b
  if (bEmpty) return -1; // b after a
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "en-GB", { numeric: true, sensitivity: "base" });
}

// True when a row matches the search query: any column with a sortValue stringifies to text that
// contains the (already lower-cased, trimmed) query. An empty query matches everything. Pure.
export function rowMatchesSearch<T>(row: T, columns: SortableColumn<T>[], queryLower: string): boolean {
  if (queryLower === "") return true;
  for (const col of columns) {
    if (!col.sortValue) continue;
    const v = col.sortValue(row);
    if (v == null) continue;
    if (String(v).toLowerCase().includes(queryLower)) return true;
  }
  return false;
}

// Apply the search filter then the sort to a list of rows. The caller has already applied any
// page-specific filterRow predicate; this layers text search and column sort on top (search first,
// then sort the matches). Returns a NEW array; never mutates the input. Pure.
export function applyTableView<T>(rows: T[], columns: SortableColumn<T>[], view: TableView): T[] {
  const queryLower = view.search.trim().toLowerCase();
  let out = queryLower ? rows.filter((r) => rowMatchesSearch(r, columns, queryLower)) : rows.slice();

  if (view.sortKey) {
    const col = columns.find((c) => c.key === view.sortKey && c.sortValue);
    if (col?.sortValue) {
      const accessor = col.sortValue;
      const sign = view.sortDir === "desc" ? -1 : 1;
      // Stable sort: ties keep their incoming (filtered) order. Nulls stay last in BOTH directions
      // (compareSortValues already pins empties last), so a desc sort does not float blanks to the top.
      out = out
        .map((row, i) => ({ row, i }))
        .sort((x, y) => {
          const aEmpty = accessor(x.row) == null || accessor(x.row) === "";
          const bEmpty = accessor(y.row) == null || accessor(y.row) === "";
          const cmp = compareSortValues(accessor(x.row), accessor(y.row));
          // Keep empties last regardless of direction: only flip the sign for two non-empty values.
          const directed = aEmpty || bEmpty ? cmp : cmp * sign;
          return directed || x.i - y.i;
        })
        .map((w) => w.row);
    }
  }
  return out;
}

// The next sort state when a column header is clicked: a three-step cycle per column. First click on
// a column sorts ascending; second descending; third clears back to the default (load) order. Pure.
export function nextSortState(
  current: { sortKey: string | null; sortDir: SortDir },
  key: string,
): { sortKey: string | null; sortDir: SortDir } {
  if (current.sortKey !== key) return { sortKey: key, sortDir: "asc" };
  if (current.sortDir === "asc") return { sortKey: key, sortDir: "desc" };
  return { sortKey: null, sortDir: "asc" }; // third click -> default order
}
